/**
 * Catalog detail — read-only view of a single template, with tabbed module
 * navigation and tier-based paywall.
 *
 * Anonymous user + non-demo template → fullscreen paywall (auth required).
 * Logged-in free + non-demo           → 3 modules visible, rest locked.
 * Public demo                         → all modules visible.
 * basic+                              → all modules visible.
 * standard                            → "Use as template" CTA is active.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Lightbulb,
  Search,
  Cog,
  Users,
  Calendar as CalendarIcon,
  Megaphone,
  Wallet,
  ShieldAlert,
  Grid3x3,
  Lock,
  Sparkles,
  Copy,
  CheckCircle2,
  ArrowUpRight,
  ChevronLeft,
  Crown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PrimaryButton, SecondaryButton, Overline } from "@/components/ui-kit";
import ReadOnlyModule from "@/components/ReadOnlyModule";
import usePageMeta from "@/hooks/usePageMeta";
import { toast } from "sonner";

const MODULES = [
  { key: "executive_summary", icon: FileText },
  { key: "canvas", icon: Grid3x3 },
  { key: "concept", icon: Lightbulb },
  { key: "market", icon: Search },
  { key: "operations", icon: Cog },
  { key: "hr", icon: Users },
  { key: "calendar", icon: CalendarIcon },
  { key: "marketing", icon: Megaphone },
  { key: "budget", icon: Wallet },
  { key: "risks", icon: ShieldAlert },
];

export default function CatalogDetail() {
  const { slug } = useParams();
  const { user, tier, refreshSubscription } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("concept");
  const [cloning, setCloning] = useState(false);
  const [picking, setPicking] = useState(false);

  usePageMeta(
    tpl
      ? `${tpl.name} — ${lang === "es" ? "Plantilla de plan de negocio" : "Business plan template"} | Atelier Business`
      : null,
    tpl?.description || null
  );

  async function load() {
    setLoading(true);
    try {
      const res = await api.get(`/catalog/${slug}`, { params: { language: lang } });
      setTpl(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      nav("/catalog");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [slug, lang]);

  async function onClone() {
    if (!tpl?.can_clone) {
      nav("/pricing");
      return;
    }
    setCloning(true);
    try {
      const res = await api.post(`/catalog/${slug}/clone`, { language: lang });
      toast.success(t("catalog.cloned_success"));
      nav(`/projects/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCloning(false);
    }
  }

  async function onPickStarterSlot() {
    setPicking(true);
    try {
      await api.post(`/catalog/${slug}/select`);
      toast.success(lang === "es" ? "Plantilla añadida a tu cupo" : "Template added to your slots");
      await refreshSubscription();
      await load();
    } catch (e) {
      const code = e.response?.data?.detail?.code;
      if (code === "no_slots_available") {
        toast.error(t("paywall.starter_slots_full"));
        nav("/pricing");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    } finally {
      setPicking(false);
    }
  }

  const isLocked = tpl?.access === "locked";
  const isQuotaExceeded = tpl?.access === "quota_exceeded";
  const isFull = tpl?.access === "full";
  const needsSelection = tpl?.access === "needs_selection";
  const isFreeRead = isFull && tpl?.is_authenticated && !tpl?.subscription_active && !tpl?.is_public_demo && !!tpl?.quota_limit;

  if (loading || !tpl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="text-[#5C5B5A] text-sm tracking-[0.2em] uppercase">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="border-b border-[#E8E6E1] bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/catalog" className="text-[#5C5B5A] hover:text-[#1A1A1A] text-sm inline-flex items-center gap-1" data-testid="back-to-catalog-btn">
              <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">{t("catalog.back_to_catalog")}</span>
            </Link>
            <div className="hidden sm:block h-6 w-px bg-[#E8E6E1]" />
            <div className="min-w-0">
              <Overline>{tpl.industry}</Overline>
              <h1
                className="text-xl sm:text-2xl tracking-tight text-[#1A1A1A] truncate"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}
                data-testid="template-title"
              >
                {tpl.hero_emoji} {tpl.name}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <LanguageSwitcher />
            {user && (
              <Link to="/" className="text-sm text-[#5C5B5A] hover:text-[#1A1A1A] hidden md:inline" data-testid="my-projects-link">
                {lang === "es" ? "Mis proyectos" : "My projects"}
              </Link>
            )}
            {!user && (
              <Link to="/login" className="text-sm text-[#5C5B5A] hover:text-[#1A1A1A]" data-testid="login-link">
                {t("auth.login")}
              </Link>
            )}
            {/* Clone / Upgrade / Pick CTA */}
            {tpl.can_clone ? (
              <PrimaryButton onClick={onClone} disabled={cloning} testId="clone-template-btn">
                <Copy className="h-4 w-4" strokeWidth={1.5} />
                {cloning ? (lang === "es" ? "Clonando..." : "Cloning...") : t("catalog.clone_button")}
              </PrimaryButton>
            ) : (
              user && !needsSelection && (
                <Link to="/pricing">
                  <SecondaryButton testId="upgrade-clone-btn">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    {lang === "es" ? "Activar plan" : "Activate plan"}
                  </SecondaryButton>
                </Link>
              )
            )}
          </div>
        </div>
      </header>

      {/* HERO BANNER */}
      <div
        className="relative w-full overflow-hidden border-b border-[#E8E6E1]"
        style={{ backgroundColor: tpl.accent_color || "#2563EB" }}
      >
        {tpl.cover_image_url && (
          <img
            src={tpl.cover_image_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-30"
            aria-hidden
          />
        )}
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-20 text-white">
          <div className="text-4xl mb-3" aria-hidden>{tpl.hero_emoji}</div>
          <Overline>
            <span className="text-white/80">{tpl.industry}</span>
          </Overline>
          <h2
            className="mt-2 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-white"
            style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
          >
            {tpl.name}
          </h2>
          <p className="mt-4 text-base max-w-2xl text-white/90">{tpl.tagline}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {(tpl.tags || []).map((tag) => (
              <span
                key={tag}
                className="text-[10px] uppercase tracking-[0.15em] text-white/90 border border-white/30 rounded-sm px-2 py-1 bg-white/10 backdrop-blur"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* PAYWALL BANNERS */}
      {isLocked && (
        <div className="bg-[#F5EFE6] border-b border-[#B89154]/30 px-6 lg:px-12 py-4">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-[#B89154] mt-0.5 shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#1A1A1A]">{t("catalog.locked_alert")}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Link to="/login">
                <SecondaryButton testId="locked-login-btn">{t("auth.login")}</SecondaryButton>
              </Link>
              <Link to="/register">
                <PrimaryButton testId="locked-register-btn">{lang === "es" ? "Crear cuenta" : "Sign up"}</PrimaryButton>
              </Link>
            </div>
          </div>
        </div>
      )}
      {isFreeRead && (
        <div className="bg-[#F5EFE6] border-b border-[#B89154]/30 px-6 lg:px-12 py-4" data-testid="free-quota-banner">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#B89154] mt-0.5 shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#1A1A1A]">
                {lang === "es"
                  ? `Lectura gratis ${tpl.quota_used ?? 1} de ${tpl.quota_limit}. Sube a Starter (49,90 €) por 6 plantillas a elegir o a Pro (99,90 €) por acceso ilimitado.`
                  : `Free read ${tpl.quota_used ?? 1} of ${tpl.quota_limit}. Upgrade to Starter ($49.90) for 6 picks or Pro ($99.90) for unlimited access.`}
              </p>
            </div>
            <Link to="/pricing">
              <PrimaryButton testId="quota-upgrade-btn">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {t("paywall.see_plans")}
              </PrimaryButton>
            </Link>
          </div>
        </div>
      )}
      {isFull && tpl.is_public_demo && !user && (
        <div className="bg-[#EFF4FF] border-b border-[#2563EB]/20 px-6 lg:px-12 py-4">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#2563EB] mt-0.5 shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#1A1A1A]">{t("catalog.viewing_demo")}</p>
            </div>
            <Link to="/register">
              <PrimaryButton testId="demo-register-btn">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {lang === "es" ? "Crear cuenta gratis" : "Create free account"}
              </PrimaryButton>
            </Link>
          </div>
        </div>
      )}

      {tpl.seed_pending ? (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="border border-[#E8E6E1] bg-white rounded-sm p-12 text-center" data-testid="seed-pending-card">
            <Sparkles className="h-10 w-10 text-[#B89154] mx-auto" strokeWidth={1.2} />
            <h3 className="mt-4 text-2xl text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
              {t("catalog.coming_soon")}
            </h3>
            <p className="mt-2 text-sm text-[#5C5B5A] max-w-md mx-auto">
              {t("catalog.seed_pending")}
            </p>
            <div className="mt-6">
              <Link to="/catalog">
                <SecondaryButton testId="back-catalog-btn">
                  <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  {t("catalog.back_to_catalog")}
                </SecondaryButton>
              </Link>
            </div>
          </div>
        </div>
      ) : needsSelection ? (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 relative" data-testid="needs-selection-section">
          {/* Blurred mock content behind the picker card */}
          <div className="absolute inset-x-6 lg:inset-x-12 top-10 bottom-6 overflow-hidden pointer-events-none select-none space-y-4 blur-[6px] opacity-40" aria-hidden>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-white border border-[#E8E6E1] rounded-sm" />
            ))}
          </div>
          <div className="relative max-w-xl mx-auto border border-[#2563EB]/30 bg-white rounded-sm p-10 text-center shadow-lg" data-testid="needs-selection-card">
            <Sparkles className="h-10 w-10 text-[#2563EB] mx-auto" strokeWidth={1.2} />
            <div className="mt-4">
              <Overline>
                {lang === "es"
                  ? `Plantillas elegidas: ${tpl.selected_slots_used ?? 0} de ${tpl.selected_slots_total ?? 6}`
                  : `Templates picked: ${tpl.selected_slots_used ?? 0} of ${tpl.selected_slots_total ?? 6}`}
              </Overline>
            </div>
            <h3 className="mt-2 text-2xl text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
              {t("paywall.starter_pick_title")}
            </h3>
            <p className="mt-3 text-sm text-[#5C5B5A] max-w-md mx-auto">
              {t("paywall.starter_pick_desc")}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <PrimaryButton onClick={onPickStarterSlot} disabled={picking} testId="pick-starter-slot-btn">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                {picking ? (lang === "es" ? "Añadiendo…" : "Adding…") : t("paywall.starter_pick_cta")}
              </PrimaryButton>
              <Link to="/catalog">
                <SecondaryButton testId="needs-selection-back-btn">
                  <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  {t("catalog.back_to_catalog")}
                </SecondaryButton>
              </Link>
            </div>
            <p className="mt-4 text-xs text-[#8A8886]">
              {lang === "es"
                ? "¿Necesitas más de 6 plantillas? "
                : "Need more than 6 templates? "}
              <Link to="/pricing" className="text-[#2563EB] underline">
                {lang === "es" ? "Sube a Pro — ilimitado" : "Upgrade to Pro — unlimited"}
              </Link>
            </p>
          </div>
        </div>
      ) : isQuotaExceeded ? (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 relative" data-testid="quota-exceeded-section">
          {/* Blurred mock content behind the paywall card */}
          <div className="absolute inset-x-6 lg:inset-x-12 top-10 bottom-6 overflow-hidden pointer-events-none select-none space-y-4 blur-[6px] opacity-40" aria-hidden>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-white border border-[#E8E6E1] rounded-sm" />
            ))}
          </div>
          <div className="relative max-w-xl mx-auto border border-[#B89154]/40 bg-white rounded-sm p-10 text-center shadow-lg" data-testid="quota-exceeded-card">
            {tpl.locked_reason === "starter_slots_full" ? (
              <>
                <Crown className="h-10 w-10 text-[#B89154] mx-auto" strokeWidth={1.2} />
                <div className="mt-4">
                  <Overline>
                    {lang === "es"
                      ? `Plantillas elegidas: ${tpl.selected_slots_used ?? 6} de ${tpl.selected_slots_total ?? 6}`
                      : `Templates picked: ${tpl.selected_slots_used ?? 6} of ${tpl.selected_slots_total ?? 6}`}
                  </Overline>
                </div>
                <h3 className="mt-2 text-2xl text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                  {lang === "es" ? "Has usado tus 6 plantillas Starter" : "You've used all 6 Starter templates"}
                </h3>
                <p className="mt-3 text-sm text-[#5C5B5A] max-w-md mx-auto">
                  {lang === "es"
                    ? "Sube a Pro (99,90 €/mes) para acceso ilimitado a las 12 plantillas y hasta 10 business plans nuevos al mes."
                    : "Upgrade to Pro ($99.90/mo) for unlimited access to all 12 templates and up to 10 new business plans per month."}
                </p>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <PrimaryButton onClick={() => nav("/pricing")} testId="quota-unlock-btn">
                    <Crown className="h-4 w-4" strokeWidth={1.5} />
                    {t("paywall.upgrade_pro")}
                  </PrimaryButton>
                  <Link to="/catalog">
                    <SecondaryButton testId="quota-back-catalog-btn">
                      <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                      {t("catalog.back_to_catalog")}
                    </SecondaryButton>
                  </Link>
                </div>
              </>
            ) : (
              <>
                <Lock className="h-10 w-10 text-[#B89154] mx-auto" strokeWidth={1.2} />
                <div className="mt-4">
                  <Overline>
                    {lang === "es"
                      ? `Lecturas gratis: ${tpl.quota_used ?? 2} de ${tpl.quota_limit ?? 2} usadas`
                      : `Free reads: ${tpl.quota_used ?? 2} of ${tpl.quota_limit ?? 2} used`}
                  </Overline>
                </div>
                <h3 className="mt-2 text-2xl text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                  {lang === "es" ? "Has usado tus 2 lecturas gratis" : "You've used your 2 free reads"}
                </h3>
                <p className="mt-3 text-sm text-[#5C5B5A] max-w-md mx-auto">
                  {lang === "es"
                    ? "Sube a Starter (49,90 €/mes) por 6 plantillas a elegir o a Pro (99,90 €/mes) por acceso ilimitado."
                    : "Upgrade to Starter ($49.90/mo) for 6 picks or Pro ($99.90/mo) for unlimited access."}
                </p>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <PrimaryButton onClick={() => nav("/pricing")} testId="quota-unlock-btn">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    {t("paywall.see_plans")}
                  </PrimaryButton>
                  <Link to="/catalog">
                    <SecondaryButton testId="quota-back-catalog-btn">
                      <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                      {t("catalog.back_to_catalog")}
                    </SecondaryButton>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      ) : isLocked && !tpl.modules ? (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="border border-[#E8E6E1] bg-white rounded-sm p-12 text-center">
            <Lock className="h-10 w-10 text-[#B89154] mx-auto" strokeWidth={1.2} />
            <h3 className="mt-4 text-2xl text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
              {lang === "es" ? "Plan bloqueado" : "Plan locked"}
            </h3>
            <p className="mt-2 text-sm text-[#5C5B5A] max-w-md mx-auto">
              {lang === "es"
                ? "Crea una cuenta gratis para previsualizar 3 módulos de este plan. Suscríbete a Basic para ver el plan completo."
                : "Create a free account to preview 3 modules of this plan. Subscribe to Basic to unlock the full plan."}
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Link to="/login"><SecondaryButton>{t("auth.login")}</SecondaryButton></Link>
              <Link to="/register"><PrimaryButton>{lang === "es" ? "Crear cuenta" : "Sign up"}</PrimaryButton></Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 grid lg:grid-cols-[260px_1fr] gap-10">
          {/* SIDEBAR */}
          <aside className="lg:sticky lg:top-24 self-start">
            <Overline>{lang === "es" ? "Módulos" : "Modules"}</Overline>
            <nav className="mt-4 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible" data-testid="catalog-module-nav">
              {MODULES.map((m, idx) => {
                const Icon = m.icon;
                const isActive = m.key === active;
                return (
                  <button
                    key={m.key}
                    onClick={() => setActive(m.key)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm border-l-2 transition-all whitespace-nowrap ${
                      isActive
                        ? "bg-white border-[#2563EB] text-[#2563EB] font-medium"
                        : "border-transparent text-[#5C5B5A] hover:text-[#1A1A1A] hover:bg-white/60"
                    }`}
                    data-testid={`catalog-module-tab-${m.key}`}
                  >
                    <span
                      className="font-mono text-xs text-[#8A8886]"
                      style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                    >
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                    <span className="flex-1 text-left">{t(`module.${m.key}`)}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* CONTENT */}
          <section className="space-y-6 min-w-0" data-testid="catalog-module-content">
            {(() => {
              const moduleData = tpl.modules?.[active];
              if (!moduleData) {
                return (
                  <div className="bg-white border border-[#E8E6E1] rounded-sm p-12 text-center">
                    <p className="text-sm text-[#5C5B5A]">
                      {lang === "es" ? "Sin contenido para este módulo." : "No content for this module."}
                    </p>
                  </div>
                );
              }
              return <ReadOnlyModule moduleKey={active} data={moduleData} project={{ name: tpl.name, description: tpl.description }} />;
            })()}

            {/* Bottom CTA for cloning */}
            {!tpl.can_clone && user && tpl.access === "full" && (
              <div className="mt-12 bg-[#EFF4FF] border border-[#2563EB]/20 rounded-sm p-8 text-center">
                <Overline>{lang === "es" ? "Listo para crearlo" : "Ready to build it"}</Overline>
                <h3 className="mt-3 text-2xl text-[#1A1A1A]" style={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                  {lang === "es" ? "Usa esta plantilla como punto de partida" : "Use this template as your starting point"}
                </h3>
                <p className="mt-2 text-sm text-[#5C5B5A] max-w-xl mx-auto">
                  {lang === "es"
                    ? "Activa Starter (49,90 €/mes) o Pro (99,90 €/mes) para clonar cualquier plantilla y empezar tu propio business plan con todo este contenido pre-rellenado."
                    : "Activate Starter ($49.90/mo) or Pro ($99.90/mo) to clone any template and start your own business plan with all this content pre-filled."}
                </p>
                <div className="mt-5">
                  <Link to="/pricing">
                    <PrimaryButton testId="bottom-upgrade-btn">
                      <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                      {t("paywall.see_plans")}
                      <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                    </PrimaryButton>
                  </Link>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
